/* eslint-disable react/jsx-props-no-spreading */
import type { ReactNode } from 'react';
import React from 'react';
import styled from 'styled-components';

import type { Spacing } from 'components-stateless/DesignSystem/Atoms/Spacing';
import { Space } from 'components-stateless/DesignSystem/Atoms/Spacing';
import {
  H1Base,
  H2Base,
  InputDescription,
} from 'components-stateless/DesignSystem/Atoms/Fonts';
import type { BoxProps } from '../Layout/Box';
import Box from '../Layout/Box';

const SectionWrapper = styled(Box)<{ maxWidth?: string }>`
  display: flex;
  flex-direction: column;
  max-width: ${({ maxWidth }) => maxWidth || 'auto'};
`;

const RowWrap = styled.div<{
  space?: (typeof Spacing)[number];
  bottom?: (typeof Spacing)[number];
}>`
  display: flex;
  flex-direction: column;
  gap: ${Space(8)};
  padding-bottom: ${Space(8)};
`;

const HeadingRow = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
`;

const Headings = {
  h1: H1Base,
  h2: H2Base,
};

type HeadingSize = keyof typeof Headings;

const getHeadingComponent = (size: HeadingSize) => styled(Headings[size])`
  flex-grow: 1;
  line-height: 40px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${Space(8)};
`;

type PageProps = {
  heading: ReactNode;
  headingActions?: ReactNode;
  headingDesc?: ReactNode;
  headingSize?: HeadingSize;
  children: ReactNode;
  maxWidth?: string;
  minWidth?: string;
} & BoxProps;

const Page = ({
  heading,
  headingActions,
  headingDesc,
  headingSize = 'h1',
  children,
  maxWidth,
  ...rest
}: PageProps) => {
  const Heading = getHeadingComponent(headingSize);

  return (
    <SectionWrapper
      paddingRight={20}
      paddingLeft={20}
      gap={16}
      maxWidth={maxWidth}
      {...rest}
    >
      <RowWrap>
        <HeadingRow>
          <Heading>{heading}</Heading>
          {headingActions && <Actions>{headingActions}</Actions>}
        </HeadingRow>
        {headingDesc && <InputDescription>{headingDesc}</InputDescription>}
      </RowWrap>
      {children}
    </SectionWrapper>
  );
};

export default Page;
